import { computed, Injectable, Signal, Type, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IrisDmsSettingsAlias, IrisDmsUserSettingsResponseI } from '@iris/common/modules/dms/models/IrisDmsSettings';
import { chain } from 'lodash';
import { IrisDmsSettingsSandbox } from '@iris/common/modules/dms/sandbox/dms-settings.sandbox';
import { IrisDmsGridNameTemplateComponent } from './dms-grid-name-template/dms-grid-name-template.component';
import { IrisDmsGridTagsTemplateComponent } from './dms-grid-tags-template/dms-grid-tags-template.component';
import { IrisFileSizePipe } from '@iris/common/modules/pipes-common/pipes/filesize/file-size.pipe';
import { IrisDmsGridActionsTemplateComponent } from './dms-grid-actions-template/dms-grid-actions-template.component';
import { IrisDmsGridCreatorTemplateComponent } from './dms-grid-creator-template/dms-grid-creator-template.component';
import { IrisTimePipe } from '@iris/common/modules/time';
import { IrisDmsFoldersSandbox } from '@iris/common/modules/dms/sandbox/dms-folders.sandbox';
import { IrisDMSFolderI, IrisDmsRecentFilesFolder } from '@iris/common/modules/dms/models/IrisDMSFolder';
import { IrisDmsFilesSandbox } from '@iris/common/modules/dms/sandbox/dms-files.sandbox';
import { IrisLanguageService } from '@iris/common/services/utils/language.service';
import { IrisDmsGridTagNameTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-tag-name-template/dms-grid-tag-name-template.component';
import { ColDef, IRowNode } from 'ag-grid-community';
import { IrisAgGridSelectorFloatingFilterComponent } from '@iris/common/modules/grid/ag-grid/components/ag-grid-selector-floating-filter/ag-grid-selector-floating-filter.component';
import { IrisAgGridFieldTextFloatingFilterComponent } from '@iris/common/modules/grid/ag-grid/components/ag-grid-field-text-floating-filter/ag-grid-field-text-floating-filter.component';
import { IrisDmsGridTrashBinNameTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-trash-bin-name-template/dms-grid-trash-bin-name-template.component';
import { IrisDmsGridTrashBinActionsTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-trash-bin-actions-template/dms-grid-trash-bin-actions-template.component';
import { dmsTagTypes, IrisDMSTagI, IrisDMSTagType } from '@iris/common/modules/dms/models/IrisDMSTag';
import { IrisDmsGridTrashBinCheckboxHeaderTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-trash-bin-checkbox-header-template/dms-grid-trash-bin-checkbox-header-template.component';
import { IrisDmsGridCheckboxTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-checkbox-template/dms-grid-checkbox-template.component';
import { IrisDmsGridCheckboxHeaderTemplateComponent } from '@iris/modules/dms/grid-templates/dms-grid-checkbox-header-template/dms-grid-checkbox-header-template.component';
import { IrisGridPinnedType } from '@iris/common/modules/grid-configuration/models/grid-configuration.settings';
import { IrisDmsGridFilterCheckboxHeaderTemplateComponent } from './dms-grid-filter-checkbox-header-template/dms-grid-filter-checkbox-header-template.component';
import { DmsFilesGridColName, DmsTagsGridColName } from '@iris/common/modules/dms/models/IrisDmsGridTypes';
import { IrisDMSFile, IrisDMSFileI } from '@iris/common/modules/dms/models/IrisDMSFile';

export enum DmsCellRenderer {
  selectionCheckbox = 'dmsSelectionCheckboxCellRenderer',
  name = 'dmsNameCellRenderer',
  tags = 'dmsTagsCellRenderer',
  creator = 'dmsCreatorRenderer',
  owner = 'dmsOwnerRenderer',
  actions = 'dmsActionsRenderer',
  trashBinName = 'dmsTrashBinName',
  trashBinActions = 'dmsTrashBinActions',
}

interface IrisDmsBaseGridI extends Pick<IrisDMSFolderI, 'id' | 'name'>, Pick<IrisDMSFileI, 'size'> {
  actions: unknown;
}
interface IrisDmsTrashBinGridI extends IrisDmsBaseGridI, Pick<IrisDMSFile, 'originalPath' | 'path' | 'removedDate' | 'updatedOn'> { }
interface IrisDmsSystemTrashFolderGridI extends IrisDmsBaseGridI, Pick<IrisDMSFolderI,  'originalName' | 'originalPath' | 'deletedOn' | 'removedDate'> {}

@Injectable()
export class IrisDmsGridConfigurationService {
  private readonly translateService = inject(TranslateService);
  private readonly dmsSettingsSandbox = inject(IrisDmsSettingsSandbox);
  private readonly fileSizePipe = inject(IrisFileSizePipe);
  private readonly timePipe = inject(IrisTimePipe);
  private readonly dmsFoldersSandbox = inject(IrisDmsFoldersSandbox);
  private readonly dmsFilesSandbox = inject(IrisDmsFilesSandbox);
  private readonly languagesService = inject(IrisLanguageService);

  get dmsGridRendererComponents(): Record<string, Type<unknown>> {
    return {
      [DmsCellRenderer.selectionCheckbox]: IrisDmsGridCheckboxTemplateComponent,
      [DmsCellRenderer.name]: IrisDmsGridNameTemplateComponent,
      [DmsCellRenderer.tags]: IrisDmsGridTagsTemplateComponent,
      [DmsCellRenderer.creator]: IrisDmsGridCreatorTemplateComponent,
      [DmsCellRenderer.actions]: IrisDmsGridActionsTemplateComponent,
      [DmsCellRenderer.trashBinName]: IrisDmsGridTrashBinNameTemplateComponent,
      [DmsCellRenderer.trashBinActions]: IrisDmsGridTrashBinActionsTemplateComponent,
    };
  }
  private readonly selectionCheckboxColDef: ColDef = {
    headerComponent: IrisDmsGridCheckboxHeaderTemplateComponent,
    cellRenderer: DmsCellRenderer.selectionCheckbox,
    width: 50,
    sortable: false,
    resizable: false,
    pinned: IrisGridPinnedType.Left,
    lockPinned: true,
    hide: false,
    colId: DmsFilesGridColName.Select,
    suppressSizeToFit: true,
  };
  private readonly nameColDef: ColDef<Pick<IrisDMSFileI, 'name'>> = {
    field: DmsFilesGridColName.Name,
    headerName: this.translateService.instant('label.Name').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Name'),
    minWidth: 300,
    flex: 1,
    cellRenderer: DmsCellRenderer.name,
    pinned: IrisGridPinnedType.Left,
    lockPinned: true,
    sortable: true,
    resizable: true,
    hide: false,
    colId: DmsFilesGridColName.Name,
    valueGetter: (params) => params.data,
  };
  private readonly extensionColDef: ColDef<Pick<IrisDMSFileI, 'extension'> & IrisDmsBaseGridI> = {
    field: DmsFilesGridColName.Extension,
    headerName: this.translateService.instant('label.FileExtension').toUpperCase(),
    headerTooltip: this.translateService.instant('label.FileExtension'),
    minWidth: 60,
    hide: true,
    sortable: true,
    resizable: true,
    valueFormatter: (params): string => params.data?.extension ?? '',
    comparator: IrisDmsGridConfigurationService.fileExtensionComparator,
  };
  private readonly tagsColDef: ColDef<Pick<IrisDMSFileI, 'tags'>> = {
    field: DmsFilesGridColName.Tags,
    headerName: this.translateService.instant('label.Tags').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Tags'),
    minWidth: 60,
    sortable: false,
    resizable: true,
    cellRenderer: DmsCellRenderer.tags,
    hide: false,
    valueGetter: (params) => params.data,
  };
  private readonly updatedOnColDef: ColDef<Pick<IrisDMSFileI, 'updatedOn'>> = {
    field: DmsFilesGridColName.UpdatedOn,
    headerName: this.translateService.instant('label.UpdatedOn').toUpperCase(),
    headerTooltip: this.translateService.instant('label.UpdatedOn'),
    minWidth: 100,
    sortable: true,
    resizable: true,
    valueFormatter: (params): string => this.timePipe.transform(params.data?.updatedOn),
    hide: false,
  };
  private readonly createdOnColDef: ColDef<Pick<IrisDMSFileI, 'createdOn'>> = {
    field: DmsFilesGridColName.CreatedOn,
    headerName: this.translateService.instant('label.CreatedOn').toUpperCase(),
    headerTooltip: this.translateService.instant('label.CreatedOn'),
    minWidth: 100,
    sortable: true,
    resizable: true,
    valueFormatter: (params): string =>
      this.timePipe.transform(params.data?.createdOn),
    hide: false,
  };
  private readonly recordingTimeColDef: ColDef<Pick<IrisDMSFileI, 'recordingTime'>> = {
    field: DmsFilesGridColName.RecordingTime,
    headerName: this.translateService.instant('label.dms.RecordingTime').toUpperCase(),
    headerTooltip: this.translateService.instant('label.dms.RecordingTime'),
    minWidth: 100,
    sortable: true,
    resizable: true,
    valueFormatter: (params): string => this.timePipe.transform(params.data?.recordingTime),
    hide: false,
  };
  private readonly createdByColDef: ColDef<Pick<IrisDMSFileI, 'createdBy'>> = {
    field: DmsFilesGridColName.CreatedBy,
    headerName: this.translateService.instant('label.Creator').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Creator'),
    minWidth: 60,
    sortable: true,
    resizable: true,
    cellRenderer: DmsCellRenderer.creator,
    hide: false,
  };
  private readonly contentLanguageColDef: ColDef<Pick<IrisDMSFileI, 'contentLanguage'>> = {
    field: DmsFilesGridColName.ContentLanguage,
    headerName: this.translateService.instant('label.Language').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Language'),
    minWidth: 100,
    sortable: false,
    resizable: true,
    valueFormatter: (params): string => this.languages(params.data?.contentLanguage ?? []),
    hide: true,
  };
  private readonly sizeColDef: ColDef<Pick<IrisDMSFileI, 'size'>> = {
    field: DmsFilesGridColName.Size,
    headerName: this.translateService.instant('label.Size').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Size'),
    minWidth: 50,
    sortable: true,
    resizable: true,
    valueFormatter: (params): string => this.fileSizePipe.transform((params.data?.size), 0),
    hide: false,
  };
  private readonly actionsColDef: ColDef<IrisDmsBaseGridI> = {
    field: DmsFilesGridColName.Actions,
    headerName: this.translateService.instant('label.Actions').toUpperCase(),
    headerTooltip: this.translateService.instant('label.Actions'),
    maxWidth: 150,
    sortable: false,
    resizable: false,
    cellRenderer: DmsCellRenderer.actions,
    pinned: IrisGridPinnedType.Right,
    lockPinned: true,
    hide: false,
    suppressSizeToFit: true,
    valueGetter: (params) => params.data,
  };
  get filesAndFoldersGridDefaultColDef(): ColDef[] {
    return [
      { ...this.selectionCheckboxColDef },
      { ...this.nameColDef },
      { ...this.extensionColDef },
      { ...this.tagsColDef },
      { ...this.updatedOnColDef },
      { ...this.createdOnColDef },
      { ...this.recordingTimeColDef },
      { ...this.createdByColDef },
      { ...this.contentLanguageColDef },
      { ...this.sizeColDef },
      { ...this.actionsColDef },
    ];
  }

  get systemTrashBinDefaultColDefs(): ColDef[] {
    return [
      { ...this.selectionCheckboxColDef },
      { ...this.nameColDef },
      {
        field: DmsFilesGridColName.OriginalName,
        headerName: this.translateService.instant('label.OriginalName').toUpperCase(),
        headerTooltip: this.translateService.instant('label.OriginalName'),
        minWidth: 220,
        resizable: true,
        sortable: true,
        sort: 'asc',
        valueFormatter: (params): string => params.data.originalName,
        hide: false,
      } as ColDef<IrisDmsSystemTrashFolderGridI>,
      {
        field: DmsFilesGridColName.OriginalPath,
        headerName: this.translateService.instant('label.OriginalFolder').toUpperCase(),
        headerTooltip: this.translateService.instant('label.OriginalFolder'),
        minWidth: 150,
        resizable: true,
        sortable: true,
        sort: 'asc',
        valueFormatter: ({ data }): string => data.originalPath,
        hide: false,
      } as ColDef<IrisDmsSystemTrashFolderGridI>,
      {
        field: DmsFilesGridColName.DeletedOn,
        headerName: this.translateService.instant('label.RemovedOn').toUpperCase(),
        headerTooltip: this.translateService.instant('label.RemovedOn'),
        minWidth: 120,
        resizable: true,
        sortable: true,
        sort: 'desc',
        valueFormatter: ({ data }): string => this.timePipe.transform(data.removedDate),
        hide: false,
      } as ColDef<IrisDmsSystemTrashFolderGridI>,
      { ...this.sizeColDef },
      { ...this.actionsColDef },
    ];
  }

  get filterDefaultColDefs(): ColDef[] {
    return [
      {
        ...this.selectionCheckboxColDef,
        headerComponent: IrisDmsGridFilterCheckboxHeaderTemplateComponent,
      },
      { ...this.nameColDef },
      { ...this.tagsColDef, hide: true },
      {
        field: DmsFilesGridColName.Path,
        headerName: this.translateService.instant('label.Path').toUpperCase(),
        headerTooltip: this.translateService.instant('label.Path'),
        sortable: false,
        minWidth: 100,
        resizable: true,
        hide: false,
      },
      { ...this.createdOnColDef },
      { ...this.recordingTimeColDef },
      { ...this.updatedOnColDef },
      { ...this.createdByColDef, hide: true },
      { ...this.sizeColDef },
      { ...this.actionsColDef },
    ];
  }

  get recentFilesFolderDefaultColDefs(): ColDef[] {
    return [
      {
        ...this.selectionCheckboxColDef,
        headerComponent: IrisDmsGridFilterCheckboxHeaderTemplateComponent,
      },
      ...this.filterDefaultColDefs.filter(col => col.colId !== DmsFilesGridColName.Select as string),
    ];
  }

  get systemFolderDefaultColDefs(): ColDef[] {
    return [
      { ...this.selectionCheckboxColDef },
      { ...this.nameColDef },
      { ...this.extensionColDef },
      { ...this.tagsColDef },
      { ...this.updatedOnColDef },
      { ...this.createdByColDef },
      { ...this.contentLanguageColDef },
      { ...this.sizeColDef },
      { ...this.actionsColDef },
    ];
  }

  get trashBinGridDefaultColDef(): ColDef[] {
    return [
      {
        ...this.selectionCheckboxColDef,
        headerComponent: IrisDmsGridTrashBinCheckboxHeaderTemplateComponent,
      },
      {
        ...this.nameColDef,
        field: DmsFilesGridColName.OriginalName,
        cellRenderer: DmsCellRenderer.trashBinName,
        colId: DmsFilesGridColName.OriginalName,
      },
      {
        field: DmsFilesGridColName.OriginalPath,
        headerName: this.translateService.instant('label.PreviousFolder').toUpperCase(),
        headerTooltip: this.translateService.instant('label.PreviousFolder'),
        minWidth: 150,
        hide: false,
        valueFormatter: ({ data }) => data.originalPath || data.path,
      } as ColDef<IrisDmsTrashBinGridI>,
      {
        field: DmsFilesGridColName.RemovedDate,
        headerName: this.translateService.instant('label.RemovedOn').toUpperCase(),
        headerTooltip: this.translateService.instant('label.RemovedOn'),
        minWidth: 120,
        hide: false,
        valueFormatter: ({ data }) => this.timePipe.transform(data.removedDate),
      } as ColDef<IrisDmsTrashBinGridI>,
      { ...this.updatedOnColDef, hide: true },
      { ...this.createdByColDef, hide: true },
      { ...this.sizeColDef },
      {
        ...this.actionsColDef,
        maxWidth: 180,
        cellRenderer: DmsCellRenderer.trashBinActions,
      },
    ];
  }

  get tagsGridDefaultColDef(): ColDef[] {
    return [
      {
        field: DmsTagsGridColName.Name,
        headerName: this.translateService.instant('label.Name').toUpperCase(),
        headerTooltip: this.translateService.instant('label.Name'),
        cellRenderer: IrisDmsGridTagNameTemplateComponent,
        floatingFilterComponent: IrisAgGridFieldTextFloatingFilterComponent,
        floatingFilterComponentParams: {
          label: 'label.dms.SearchName',
        },
        comparator: (a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()),
      },
      {
        field: DmsTagsGridColName.Type,
        headerName: this.translateService.instant('label.Type').toUpperCase(),
        headerTooltip: this.translateService.instant('label.Type'),
        valueFormatter: ({ data }) => this.translateService.instant(data.projectId ? 'label.Project' : 'label.Public'),
        filterValueGetter: ({ data }) => this.translateService.instant(data.projectId ? 'label.Project' : 'label.Public'),
        floatingFilterComponent: IrisAgGridSelectorFloatingFilterComponent,
        floatingFilterComponentParams: {
          directory: dmsTagTypes.map(({ label }) => ({ name: this.translateService.instant(label) })),
          label: this.translateService.instant('label.Filter'),
        },
        comparator: (_valueA, _valueB, nodeA, nodeB) =>
          IrisDmsGridConfigurationService.getTagType(nodeA.data).localeCompare(IrisDmsGridConfigurationService.getTagType(nodeB.data)),
      } as ColDef<IrisDMSTagI>,
    ];
  }

  readonly filesAndFoldersColumnDef = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.foldersConfig(), this.filesAndFoldersGridDefaultColDef),
  );

  readonly trashBinColumnDef = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.trashBinConfig(), this.trashBinGridDefaultColDef),
  );

  readonly filterColumnDef = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.filterConfig(), this.filterDefaultColDefs),
  );

  readonly recentFilesColumnDefs = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.recentFilesConfig(), this.recentFilesFolderDefaultColDefs),
  );

  readonly systemFolderColumnDef = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.systemFolderConfig(), this.systemFolderDefaultColDefs),
  );

  readonly systemTrashBinColumnDef = computed(() =>
    this.getColumnsDef(this.dmsSettingsSandbox.systemTrashBinConfig(), this.systemTrashBinDefaultColDefs),
  );

  columnDefByAlias(alias: IrisDmsSettingsAlias = IrisDmsSettingsAlias.Folder): Signal<ColDef[]> {
    return computed(() => {
      switch (alias) {
        case IrisDmsSettingsAlias.Trash:
          return this.trashBinColumnDef();
        case IrisDmsSettingsAlias.SystemTrash:
          return this.systemTrashBinColumnDef();
        case IrisDmsSettingsAlias.Filter:
          return this.filterColumnDef();
        case IrisDmsSettingsAlias.RecentFiles:
          return this.recentFilesColumnDefs();
        case IrisDmsSettingsAlias.SystemFolder:
          return this.systemFolderColumnDef();
        case IrisDmsSettingsAlias.Folder:
        default:
          return this.filesAndFoldersColumnDef();
      }
    });
  };

  readonly aliasBySelectedFolder = computed(() => {
    const folder = this.dmsFoldersSandbox.selectedFolder();
    const isTrashBinTabSelected = this.dmsFoldersSandbox.trashBinTabSelected();
    return isTrashBinTabSelected ? IrisDmsSettingsAlias.Trash : this.getSettingsAliasByFolder(folder)();
  });

  readonly columnDef = computed(() => {
    return this.columnDefByAlias(this.aliasBySelectedFolder())();
  });

  getSettingsAliasByFolder(folder: IrisDMSFolderI): Signal<IrisDmsSettingsAlias> {
    return computed(() => {
      const isFilterActive = this.dmsFilesSandbox.isFilterActive();
      switch (true) {
        case isFilterActive:
          return IrisDmsSettingsAlias.Filter;
        case folder?.id === IrisDmsRecentFilesFolder.id:
          return IrisDmsSettingsAlias.RecentFiles;
        case folder?.isTrashBin:
          return IrisDmsSettingsAlias.SystemTrash;
        case folder?.isSystem:
          return IrisDmsSettingsAlias.SystemFolder;
        default:
          return IrisDmsSettingsAlias.Folder;
      }
    });
  }

  readonly defaultColDefBySelectedFolder = computed(() => {
    const alias = this.aliasBySelectedFolder();
    return this.defaultColumnDefByAlias(alias)?.filter(({ field }) => !!field);
  });

  defaultColumnDefByAlias(alias: IrisDmsSettingsAlias = IrisDmsSettingsAlias.Folder): ColDef[] {
    switch (alias) {
      case IrisDmsSettingsAlias.Trash:
        return this.trashBinGridDefaultColDef;
      case IrisDmsSettingsAlias.SystemTrash:
        return this.systemTrashBinDefaultColDefs;
      case IrisDmsSettingsAlias.Filter:
      case IrisDmsSettingsAlias.RecentFiles:
        return this.recentFilesFolderDefaultColDefs;
      case IrisDmsSettingsAlias.SystemFolder:
      case IrisDmsSettingsAlias.Folder:
      default:
        return this.filesAndFoldersGridDefaultColDef;
    }
  }

  constructor() {
    this.dmsSettingsSandbox.getFolderSettings();
  }

  private getColumnsDef(config: IrisDmsUserSettingsResponseI, defaultColDefs: ColDef[]): ColDef[] {
    const columns = config?.settings?.columns ?? [];
    const colDefs = chain(defaultColDefs)
      .map((defaultColumn: ColDef) => {
        const targetColIndex = columns.findIndex(column => column.field === defaultColumn.field);
        let sortIndex = targetColIndex;

        if (defaultColumn.pinned) {
          sortIndex = defaultColumn.pinned === IrisGridPinnedType.Left ? -1 : defaultColDefs.length;
        }

        return {
          ...defaultColumn,
          sortIndex,
          hide: columns[targetColIndex]?.hide ?? defaultColumn.hide,
        };
      })
      .orderBy('sortIndex')
      .value();

    let resizeableColumnIndex = colDefs.findLastIndex(column => !column.hide && !column.pinned);
    if (resizeableColumnIndex === -1) {
      // -1 is taken here to skip actions column which always stays at the end
      resizeableColumnIndex = colDefs.slice(0, -1).findLastIndex(column => !column.hide);
    }
    colDefs[resizeableColumnIndex].suppressSizeToFit = false;
    return colDefs;
  }

  private languages(contentLanguages: string[]): string {
    const languages = this.languagesService.getLanguagesNames(contentLanguages);
    return languages.length ? languages.join(', ') : this.translateService.instant('label.NotSet');
  }

  private static getTagType(tag: IrisDMSTagI): IrisDMSTagType {
    return tag.projectId ? IrisDMSTagType.Project : IrisDMSTagType.Public;
  }

  private static fileExtensionComparator(valueA: string, valueB: string, nodeA: IRowNode<IrisDmsBaseGridI>, nodeB: IRowNode<IrisDmsBaseGridI>): number {
    const valueAExt = valueA || nodeA.data.name.includes('.') ? nodeA.data.name.split('.').pop() : '';
    const valueBExt = valueB || nodeB.data.name.includes('.') ? nodeB.data.name.split('.').pop() : '';
    return valueAExt.localeCompare(valueBExt);
  }
}
